/**
 * Service for handling push notifications
 * Uses Web Notifications API and Firebase Cloud Messaging
 */

import type { Notification } from "../types";

/**
 * Check if the browser supports notifications
 */
export const isPushNotificationSupported = (): boolean => {
  return "Notification" in window && "serviceWorker" in navigator;
};

/**
 * Request permission for push notifications
 */
export const requestPushNotificationPermission = async (): Promise<boolean> => {
  if (!isPushNotificationSupported()) {
    console.warn("Push notifications are not supported in this browser");
    return false;
  }

  if (Notification.permission === "granted") {
    return true;
  }

  if (Notification.permission !== "denied") {
    const permission = await Notification.requestPermission();
    return permission === "granted";
  }

  return false;
};

/**
 * Check if user has notification permission enabled
 */
export const hasPushNotificationPermission = (): boolean => {
  return isPushNotificationSupported() && Notification.permission === "granted";
};

/**
 * Send a push notification
 */
export const sendPushNotification = (
  title: string,
  options?: NotificationOptions,
): void => {
  if (!hasPushNotificationPermission()) {
    console.log("Push notifications not permitted");
    return;
  }

  if (navigator.serviceWorker && navigator.serviceWorker.controller) {
    navigator.serviceWorker.controller.postMessage({
      type: "SHOW_NOTIFICATION",
      title,
      options: {
        icon: "/logo.png",
        tag: "notification",
        requireInteraction: false,
        ...options,
      },
    });
  }
};

/**
 * Send a notification based on the notification object
 */
export const sendNotification = (notification: Notification): void => {
  if (!hasPushNotificationPermission()) {
    return;
  }

  let title = "";
  let body = "";

  if (notification.type === "friend-request") {
    title = "Friend Request";
    body = `${notification.fromDisplayName || notification.fromEmail} sent you a friend request`;
  } else if (notification.type === "share-request") {
    title = "Share Request";
    body = `${notification.fromDisplayName || notification.fromEmail} wants to share "${notification.itemTitle}"`;
  }

  sendPushNotification(title, {
    body,
    tag: `notification-${notification.id}`,
    data: {
      notificationId: notification.id,
      type: notification.type,
    },
  });
};

/**
 * Store the user's notification preference in localStorage
 */
export const setNotificationPreference = (enabled: boolean): void => {
  localStorage.setItem("pushNotificationsEnabled", JSON.stringify(enabled));
};

/**
 * Get the user's notification preference from localStorage
 */
export const getNotificationPreference = (): boolean => {
  const pref = localStorage.getItem("pushNotificationsEnabled");
  return pref !== null ? JSON.parse(pref) : hasPushNotificationPermission();
};

/**
 * Initialize push notifications for a user
 */
export const initializePushNotifications = async (): Promise<void> => {
  if (!isPushNotificationSupported()) {
    console.log("Push notifications not supported");
    return;
  }

  // Only request permission if user hasn't already seen the prompt
  if (Notification.permission === "default") {
    const allowed = await requestPushNotificationPermission();
    setNotificationPreference(allowed);
  } else {
    setNotificationPreference(Notification.permission === "granted");
  }
};
